import { useState, useEffect, useRef, useCallback } from 'react';

const DERIV_PUBLIC_WEBSOCKET_URL = 'wss://api.derivws.com/trading/v1/options/ws/public';

interface PatternItem {
  char: string;
  class: string;
  tooltip?: string;
}

interface WebSocketData {
  currentValue: string;
  evenCount: number;
  oddCount: number;
  evenRawCount: number;
  oddRawCount: number;
  pattern: PatternItem[];
  chartData: any;
  digitPercentages: number[];
}

interface UseWebSocketOptions {
  market: string;
  contractType: string;
  ticks: number;
  overValue: number;
  underValue: number;
}

export const useWebSocket = (options: UseWebSocketOptions) => {
  const [data, setData] = useState<WebSocketData>({
    currentValue: '0',
    evenCount: 0,
    oddCount: 0,
    evenRawCount: 0,
    oddRawCount: 0,
    pattern: [],
    chartData: {},
    digitPercentages: Array(10).fill(0),
  });
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pipSize, setPipSize] = useState<number>(2); // Default to 2 decimals

  const wsRef = useRef<WebSocket | null>(null);
  const ticksCurrentRef = useRef<number[]>([]);
  const digitWindowRef = useRef<number[]>([]);
  const subIdRef = useRef<string>('774cbfef-72c8-c8e4-42c5-189e941d2379');
  const previousRef = useRef<number>(0);

  // Reconnection logic
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldReconnectRef = useRef(true);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // ms

  // Fetch pip size from Deriv API active_symbols
  useEffect(() => {
    const fetchPipSize = async () => {
      try {
        const response = await fetch('https://api.deriv.com/api/v2/active_symbols?brief=1&product=basic');
        const result = await response.json();
        if (result.active_symbols) {
          const symbol = result.active_symbols.find((s: any) => s.symbol === options.market);
          if (symbol && symbol.pip) {
            // pip is a string like "0.01" or "0.001"
            const decimals = symbol.pip.split('.')[1]?.length || 0;
            setPipSize(decimals);
          } else {
            setPipSize(2); // fallback
          }
        }
      } catch (err) {
        setPipSize(2); // fallback
      }
    };
    fetchPipSize();
  }, [options.market]);

  // Reference-style digit extraction
  const getLastDigit = useCallback((tick: number, pipSize: number) => {
    const lastDigit = tick.toFixed(pipSize).slice(-1);
    return Number(lastDigit);
  }, []);

  const formatQuote = useCallback((quote: number, decimals: number): string => {
    // Format quote to correct decimals using pip size
    return quote.toFixed(decimals);
  }, []);

  const findSmallest = useCallback((arr: number[]) => {
    let smallest = arr[0], index = 0;
    for (let i = 1; i < arr.length; i++) {
      if (arr[i] < smallest) {
        smallest = arr[i];
        index = i;
      }
    }
    return { smallest, index };
  }, []);

  const findLargest = useCallback((arr: number[]) => {
    let largest = arr[0], index = 0;
    for (let i = 1; i < arr.length; i++) {
      if (arr[i] > largest) {
        largest = arr[i];
        index = i;
      }
    }
    return { largest, index };
  }, []);

  const updateStats = useCallback((value1: number, value2: number) => {
    const total = value1 + value2;
    const percent1 = total > 0 ? ((value1 / total) * 100).toFixed(1) : '0';
    const percent2 = total > 0 ? ((value2 / total) * 100).toFixed(1) : '0';

    setData(prev => ({
      ...prev,
      evenCount: parseFloat(percent1),
      oddCount: parseFloat(percent2),
      evenRawCount: value1,
      oddRawCount: value2,
      chartData: {
        labels: prev.chartData.labels || ['Even', 'Odd'],
        datasets: [{
          data: [parseFloat(percent1), parseFloat(percent2)],
          backgroundColor: prev.chartData.datasets?.[0]?.backgroundColor || ['#3b82f6', '#ef4444'],
          borderColor: prev.chartData.datasets?.[0]?.borderColor || ['#3b82f6', '#ef4444'],
        }],
      },
    }));
  }, []);

  const updateDigitStats = useCallback((digits: number[]) => {
    const total = digits.reduce((a, b) => a + b, 0);
    const percentages = digits.map(d => total > 0 ? ((d / total) * 100).toFixed(1) : '0');

    const resultS = findSmallest(digits);
    const resultL = findLargest(digits);

    setData(prev => ({
      ...prev,
      evenCount: resultL.index,
      oddCount: resultS.index,
      chartData: {
        labels: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
        datasets: [{
          data: percentages.map(p => parseFloat(p)),
          backgroundColor: Array(10).fill('#3b82f6').map((color, i) =>
            i === resultS.index ? '#ef4444' :
            i === resultL.index ? '#10b981' : color
          ),
          borderColor: Array(10).fill('#3b82f6').map((color, i) =>
            i === resultS.index ? '#ef4444' :
            i === resultL.index ? '#10b981' : color
          ),
        }],
      },
    }));
  }, [findSmallest, findLargest]);

  const updateDigitPercentages = useCallback((digits: number[]) => {
    const counts = Array(10).fill(0);
    digits.forEach(digit => counts[digit]++);
    const total = digits.length;
    setData(prev => ({
      ...prev,
      digitPercentages: counts.map(count => total ? Number(((count / total) * 100).toFixed(2)) : 0),
    }));
  }, []);

  const updatePatternDisplay = useCallback((pattern: PatternItem[]) => {
    setData(prev => ({
      ...prev,
      pattern: pattern.slice(-50),
    }));
  }, []);


  const connectWebSocket = useCallback(() => {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ws = new WebSocket(DERIV_PUBLIC_WEBSOCKET_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsLoading(false);
        reconnectAttemptsRef.current = 0; // Reset on successful connection
        // Start by requesting historical data
        ws.send(JSON.stringify({
          ticks_history: options.market,
          count: Math.max(options.ticks, 1000),
          end: 'latest',
          adjust_start_time: 1,
          style: 'ticks',
        }));
      };

      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);

          // Handle direct data requests (no authorization needed for basic market data)
          if (data.msg_type === 'history') {
            const historyDigits = data.history.prices.map((price: number) => getLastDigit(price, pipSize));
            const analysisPrices = data.history.prices.slice(-options.ticks);
            ticksCurrentRef.current = historyDigits.slice(-options.ticks);
            digitWindowRef.current = historyDigits.slice(-1000);
            updateDigitPercentages(digitWindowRef.current);

            // Process historical data based on contract type
            if (options.contractType === '0') { // Even/Odd
              let even = 0, odd = 0;
              ticksCurrentRef.current.forEach(d => d % 2 === 0 ? even++ : odd++);
              updateStats(even, odd);
            } else if (options.contractType === '1') { // Rise/Fall
              let rise = 0, fall = 0;
              for (let i = 1; i < ticksCurrentRef.current.length; i++) {
                const prev = analysisPrices[i - 1];
                const curr = analysisPrices[i];
                if (prev > curr) fall++;
                else if (prev < curr) rise++;
              }
              previousRef.current = data.history.prices[data.history.prices.length - 1];
              updateStats(rise, fall);
            } else if (options.contractType === '2') { // Over/Under
              let over = 0, under = 0;
              ticksCurrentRef.current.forEach(d => {
                if (d > options.overValue) over++;
                if (d < options.underValue) under++;
              });
              updateStats(over, under);
            } else if (options.contractType === '3') { // Digit Frequency
              const digits = Array(10).fill(0);
              ticksCurrentRef.current.forEach(d => digits[d]++);
              updateDigitStats(digits);
            }

            ws.send(JSON.stringify({ ticks: options.market, subscribe: 1 }));
          }
          else if (data.msg_type === 'tick') {
            if (data.echo_req.ticks === options.market) {
              subIdRef.current = data.subscription.id;
              const quote = data.tick.quote;

              // Format current value display
              // Format current value display using dynamic pip size
              let formattedQuote = formatQuote(quote, pipSize);
              setData(prev => ({ ...prev, currentValue: formattedQuote }));

              // Digit extraction using pip size
              let digit = getLastDigit(quote, pipSize);

              // Keep the selected analysis window current.
              if (ticksCurrentRef.current.length >= options.ticks) {
                ticksCurrentRef.current.shift();
              }
              ticksCurrentRef.current.push(digit);
              if (digitWindowRef.current.length >= 1000) {
                digitWindowRef.current.shift();
              }
              digitWindowRef.current.push(digit);
              updateDigitPercentages(digitWindowRef.current);

              // Update pattern and stats based on contract type
              if (options.contractType === '0') { // Even/Odd
                let even = 0, odd = 0;
                ticksCurrentRef.current.forEach(d => d % 2 === 0 ? even++ : odd++);
                updateStats(even, odd);

                const pattern = ticksCurrentRef.current.slice(-50).map(d => ({
                  char: d % 2 === 0 ? 'E' : 'O',
                  class: d % 2 === 0 ? 'E' : 'O',
                  tooltip: `Digit: ${d} (${d % 2 === 0 ? 'Even' : 'Odd'})`
                }));
                updatePatternDisplay(pattern);
              } else if (options.contractType === '1') { // Rise/Fall
                let rise = 0, fall = 0;
                for (let i = 1; i < ticksCurrentRef.current.length; i++) {
                  const prev = ticksCurrentRef.current[i - 1];
                  const curr = ticksCurrentRef.current[i];
                  if (prev > curr) fall++;
                  else if (prev < curr) rise++;
                }
                updateStats(rise, fall);

                const pattern = [];
                for (let i = 1; i < ticksCurrentRef.current.length; i++) {
                  pattern.push({
                    char: ticksCurrentRef.current[i - 1] > ticksCurrentRef.current[i] ? 'F' : 'R',
                    class: ticksCurrentRef.current[i - 1] > ticksCurrentRef.current[i] ? 'F' : 'R',
                    tooltip: `From ${ticksCurrentRef.current[i - 1]} to ${ticksCurrentRef.current[i]}`
                  });
                }
                updatePatternDisplay(pattern);
              } else if (options.contractType === '2') { // Over/Under
                let over = 0, under = 0;
                ticksCurrentRef.current.forEach(d => {
                  if (d > options.overValue) over++;
                  if (d < options.underValue) under++;
                });
                updateStats(over, under);

                const pattern = ticksCurrentRef.current.slice(-50).map(d => ({
                  char: d > options.overValue ? 'O' : (d < options.underValue ? 'U' : '-'),
                  class: d > options.overValue ? 'Ov' : (d < options.underValue ? 'U' : '-'),
                  tooltip: `Digit: ${d} (${d > options.overValue ? 'Over' : d < options.underValue ? 'Under' : 'Neutral'})`
                }));
                updatePatternDisplay(pattern);
              } else if (options.contractType === '3') { // Digit Frequency
                const digits = Array(10).fill(0);
                ticksCurrentRef.current.forEach(d => digits[d]++);
                updateDigitStats(digits);

                const pattern = ticksCurrentRef.current.slice(-50).map(d => ({
                  char: String(d),
                  class: `digit-${d}`,
                  tooltip: `Digit: ${d}`
                }));
                updatePatternDisplay(pattern);
              }
            }
          }
          else if (data.error) {
            setError(`WebSocket error: ${data.error.message}`);
            setIsLoading(false);
          }
        } catch (err) {
          setError('Error processing WebSocket message');
          setIsLoading(false);
        }
      };

      ws.onerror = (error) => {
        if (wsRef.current !== ws) return;
        setError('WebSocket connection error');
        setIsConnected(false);
        setIsLoading(false);
        // Attempt to reconnect
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current); // Exponential backoff
          reconnectAttemptsRef.current++;
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else {
          setError('Max reconnect attempts reached. Please refresh or check your connection.');
        }
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        setIsConnected(false);
        setIsLoading(false);
        // Attempt to reconnect if not intentionally closed
        if (shouldReconnectRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          reconnectAttemptsRef.current++;
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else {
          setError('Max reconnect attempts reached. Please refresh or check your connection.');
        }
      };

    } catch (err) {
      setError('Failed to create WebSocket connection');
      setIsLoading(false);
    }
  }, [options, updateStats, updateDigitStats, updateDigitPercentages, updatePatternDisplay, formatQuote, getLastDigit, pipSize]);

  useEffect(() => {
    // When market, ticks, or contract type changes, we need to close the current connection and create a new one
    // because WebSocket subscriptions are market-specific and contract type affects data processing
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      shouldReconnectRef.current = false;
      wsRef.current.close();
      wsRef.current = null;
      setIsConnected(false);
    }

    // Reset reconnect attempts on options change
    reconnectAttemptsRef.current = 0;
    shouldReconnectRef.current = true;
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);

    // Small delay before reconnecting to prevent rapid connections
    const timer = setTimeout(() => {
      connectWebSocket();
    }, 300); // Increased delay

    return () => {
      shouldReconnectRef.current = false;
      clearTimeout(timer);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.market, options.ticks, options.contractType]); // connectWebSocket is stable via useCallback

  // Separate effect for cleanup on unmount
  useEffect(() => {
    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  return { data, isConnected, isLoading, error };
};
